import { ListAlt } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import React from 'react';
import { ResponsiveContainer, Tooltip, Treemap } from 'recharts';
import { ScrollableListPopover } from '../../../Common/ScrollableListPopoverForTreeCharts';
import type { AssetData } from '../utils';
import { CustomizedContent } from './TreeChartCellRenderer';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const COLORS = ['#4683E6'];

const CustomTooltip: React.FC<{
  active?: boolean;
  payload?: any;
  label?: string;
  enableDarkTheme?: boolean;
}> = ({ active, payload, label, enableDarkTheme }) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  if (active && payload && payload.length) {
    const hoverCell = payload[0].payload;
    const totalCost = hoverCell?.total_cost || 0;
    const costPerTon = `$${hoverCell?.costPerTon}` || 'Not Available';
    const costPerEngineHr = `$${hoverCell?.costPerEngineHr}` || 'Not Available';
    const costPerMile = `$${hoverCell?.costPerMile}` || 'Not Available';

    return (
      <>
        <Box
          sx={{
            backgroundColor: enableDarkTheme
              ? palette.pageBackground
              : palette.pageBackground,
            padding: '10px',
          }}
        >
          <Typography
            sx={{
              color: palette.textSecondary,
            }}
            variant="body2"
          >
            {t('asset')} {hoverCell.name}
          </Typography>
          <Typography
            sx={{
              color: palette.textSecondary,
            }}
            variant="body2"
          >
            {t('total_cost_2')} {totalCost ? `$${totalCost}` : 'N/A'}{' '}
          </Typography>
          <Typography
            sx={{
              color: palette.textSecondary,
            }}
            variant="body2"
          >
            {t('cost_per_tonnage')} {costPerTon}{' '}
          </Typography>
          <Typography
            sx={{
              color: palette.textSecondary,
            }}
            variant="body2"
          >
            {t('cost_per_engine_hr')} {costPerEngineHr}{' '}
          </Typography>
          <Typography
            sx={{
              color: palette.textSecondary,
            }}
            variant="body2"
          >
            {t('cost_per_mile')} {costPerMile}{' '}
          </Typography>
        </Box>
      </>
    );
  }

  return null;
};

const ListViewButton = ({
  data,
  enableDarkTheme,
}: {
  data: AssetData[];
  enableDarkTheme?: boolean;
}) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleOpenList = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseList = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  return (
    <Box display="flex" justifyContent="flex-end" mb={1}>
      <Button
        sx={{
          color: palette.textPrimary,
          border: `1px solid ${palette.primary}`,
          textTransform: 'none',
          height: '30px',
        }}
        onClick={handleOpenList}
        startIcon={<ListAlt />}
      >
        {t('list_view')}
      </Button>
      {open && (
        <ScrollableListPopover
          open={open}
          anchorEl={anchorEl}
          onClose={handleCloseList}
          data={data}
          getPrimaryText={(item) => `Asset - ${item.name}`}
          getSecondaryText={(item) => `Total Cost: ${item.total_cost}`}
          enableDarkTheme={enableDarkTheme}
          width={200}
          height={400}
        />
      )}
    </Box>
  );
};

export const AssetsTreeChart = ({
  selectedCell,
  handleCellClick,
  data,
  enableDarkTheme,
}: {
  selectedCell: string[];
  handleCellClick: any;
  data: AssetData[];
  enableDarkTheme?: boolean;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ListViewButton data={data} enableDarkTheme={enableDarkTheme} />
      <ResponsiveContainer width="100%" height={400}>
        <Treemap
          // width={500}
          // height={600}
          data={data}
          dataKey="totalCostForDisplay"
          aspectRatio={4 / 3}
          stroke="#fff"
          content={
            // @ts-expect-error error exists in ts with chart library
            <CustomizedContent
              colors={COLORS}
              onClick={handleCellClick}
              selectedCell={selectedCell}
              enableDarkTheme={enableDarkTheme}
              primaryColor={palette.primary}
            />
          }
        >
          <Tooltip
            content={<CustomTooltip enableDarkTheme={enableDarkTheme} />}
          />
        </Treemap>
      </ResponsiveContainer>
    </div>
  );
};
